import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"
import type { SendInvitationInput } from "../../../../api/services/invitation/types"

const inviteSchema = z.object({
  inviteeEmail: z.string().email("Invalid email address"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
})

type InviteFormData = SendInvitationInput

interface SendInviteFormProps {
  className?: string
  title?: string
}

export function SendInviteForm({ className, title = "Send new invitation" }: SendInviteFormProps) {
  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      inviteeEmail: "",
      firstName: "",
      lastName: "",
    },
  })

  const utils = trpc.useUtils()

  const sendInvitation = trpc.invitations.send.useMutation({
    onSuccess: () => {
      toast.success("Invitation sent successfully!")
      form.reset()
      utils.invitations.list.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: InviteFormData) => {
    sendInvitation.mutate(values)
  }

  return (
    <div className={`rounded-lg border p-6 ${className || ""}`}>
      <Typography as="h2" size="lg" className="mb-4">
        {title}
      </Typography>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="inviteeEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email address" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={sendInvitation.isPending}>
            {sendInvitation.isPending ? "Sending..." : "Send Invitation"}
          </Button>
        </form>
      </Form>
    </div>
  )
}
